import { daysUntilCalendarDate, instantTenantDay } from './calendar-date';
import {
  LEAD_SOURCES,
  LOST_REASONS,
  OPEN_STAGES,
  OPPORTUNITY_STAGES,
  type LeadSource,
  type LostReason,
  type OpportunityStage,
} from '../types/opportunity';

/**
 * Stage badge and rail. EXHAUSTIVE over the API's enum; an unknown value is a
 * slate "—" with a transparent rail and never a real stage. Five stages, five
 * signal hues, one each (the register idiom: dark ink on a saturated fill):
 *   lead → slate, qualified → blue, quoted → amber, won → green, lost → red.
 */
export const STAGE_LABELS: Record<OpportunityStage, string> = {
  lead: 'Lead',
  qualified: 'Qualified',
  quoted: 'Quoted',
  won: 'Won',
  lost: 'Lost',
};

export const STAGE_SKIN: Record<OpportunityStage, { label: string; fill: string; rail: string }> = {
  lead: { label: STAGE_LABELS.lead, fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  qualified: { label: STAGE_LABELS.qualified, fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  quoted: { label: STAGE_LABELS.quoted, fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  won: { label: STAGE_LABELS.won, fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  lost: { label: STAGE_LABELS.lost, fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};

export const UNKNOWN_STAGE = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

export function isOpportunityStage(v: unknown): v is OpportunityStage {
  return typeof v === 'string' && (OPPORTUNITY_STAGES as readonly string[]).includes(v);
}

export function stageSkin(stage: unknown) {
  return isOpportunityStage(stage) ? STAGE_SKIN[stage] : UNKNOWN_STAGE;
}

/** Edit, stage moves and links: the owner, or admin / office_staff (opportunities.routes.ts). */
export function canChangeOpportunity(
  user: { id?: string | null; role?: string | null } | null | undefined,
  row: { ownerUserId?: string | null } | null | undefined,
): boolean {
  if (!user || !row) return false;
  if (user.role === 'admin' || user.role === 'office_staff') return true;
  return !!user.id && user.id === row.ownerUserId;
}

/** POST /quotes (and the rate-sheet / price-book creates): requireRole('admin','office_staff'). */
export function canCreateQuote(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'office_staff';
}

/** POST /jobs: JOB_WRITE_ROLES (fieldiq-api src/lib/job-write-roles.ts). */
export function canCreateJob(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'office_staff' || role === 'supervisor' || role === 'dispatcher';
}

/** Exhaustive over opportunity-write.ts LOST_REASONS. */
export const LOST_REASON_LABELS: Record<LostReason, string> = {
  price: 'Price',
  competitor: 'Went to a competitor',
  timing: 'Timing',
  no_work: 'No work',
  relationship: 'Relationship',
  other: 'Other',
};

/** Exhaustive over opportunity-write.ts LEAD_SOURCES. */
export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  referral: 'Referral',
  msa: 'MSA',
  existing_customer: 'Existing customer',
  tender: 'Tender',
  rig_activity: 'Rig activity',
  cold: 'Cold',
  other: 'Other',
};

export const isLostReason = (v: unknown): v is LostReason =>
  typeof v === 'string' && (LOST_REASONS as readonly string[]).includes(v);
export const isLeadSource = (v: unknown): v is LeadSource =>
  typeof v === 'string' && (LEAD_SOURCES as readonly string[]).includes(v);

/**
 * Lost reason as a label. Rows lost before 0542 hold FREE TEXT in lost_reason,
 * so an unrecognised value shows as sent (it is what the person wrote), never a
 * guessed code. Absent is the caller's `—`.
 */
export function lostReasonLabel(v: string | null | undefined): string | null {
  if (!v) return null;
  return isLostReason(v) ? LOST_REASON_LABELS[v] : v;
}

/** An unknown lead source is `—`: the column is a code, and a code we cannot name is not a label. */
export function leadSourceLabel(v: string | null | undefined): string {
  return isLeadSource(v) ? LEAD_SOURCE_LABELS[v] : '—';
}

export const isOpenStage = (stage: unknown): boolean =>
  typeof stage === 'string' && (OPEN_STAGES as readonly string[]).includes(stage);

/**
 * ONE deal's weighted value: estimatedValue × probability / 100, rounded to the
 * cent exactly as the API rounds each row inside /pipeline's
 * weightedValueKnownTotal (ROUND(v × p / 100.0, 2)). The API returns no
 * per-deal weighted figure, so this is not a second answer to one it sends — but
 * a STAGE total is the API's, and is never re-added from rows here.
 * An unestimated deal has no weighted value: null, rendered `—`, never $0.
 */
export function dealWeightedValue(estimatedValue: number | null | undefined, probability: number | null | undefined): number | null {
  if (estimatedValue === null || estimatedValue === undefined || !Number.isFinite(estimatedValue)) return null;
  if (probability === null || probability === undefined || !Number.isFinite(probability)) return null;
  return Math.round(estimatedValue * probability) / 100;
}

/**
 * A next step is overdue when its due day is BEFORE the tenant's today — both
 * calendar days, so it does not flip at UTC midnight. Only open deals can be
 * overdue (a won or lost deal has no next step to chase). No due day, or no
 * known zone, is NOT overdue and NOT on time: null, which callers render as no
 * badge — the absence is not a clean bill of health and is never shown as one.
 */
export function nextStepOverdue(
  row: { stage: string; nextStepDue: string | null },
  timeZone: string | null | undefined,
  now: Date = new Date(),
): boolean | null {
  if (!isOpenStage(row.stage) || !row.nextStepDue) return null;
  const days = daysUntilCalendarDate(row.nextStepDue, timeZone, now);
  return days === null ? null : days < 0;
}

/**
 * Whole tenant-calendar days the deal has sat on its current stage: since the
 * latest stage-history entry INTO that stage, else since the deal was created
 * (a deal created on a stage has no "into" row of its own on older data).
 * Null when neither instant is readable or the zone is unknown.
 */
export function daysInStage(
  row: { stage: string; createdAt: string; stageHistory?: { toStage: string; changedAt: string }[] },
  timeZone: string | null | undefined,
  now: Date = new Date(),
): number | null {
  const into = [...(row.stageHistory ?? [])].reverse().find((h) => h.toStage === row.stage);
  const day = instantTenantDay(into?.changedAt ?? row.createdAt, timeZone);
  if (!day) return null;
  const until = daysUntilCalendarDate(day, timeZone, now);
  return until === null ? null : Math.max(0, -until);
}
